import React, { useState, useEffect } from 'react';
import {
  Card, Table, Tag, Button, Space, message, Typography, Input, Modal, Switch,
  Descriptions, Alert, Tooltip,
} from 'antd';
import {
  ApiOutlined, ReloadOutlined, SaveOutlined, KeyOutlined,
  VideoCameraOutlined, SettingOutlined,
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

// WAN3.0 系列（上游 阿里云百炼 DashScope，对外隐藏上游模型名）
const WAN3_MODEL_INFO = {
  'WAN3.0': { upstream: 'wan3.0-video', note: '标准版（最长 30 秒 / 30fps）' },
  'WAN3.0-Prime': { upstream: 'wan3.0-video-prime', note: '高速版（能力对齐标准版）' },
};

const Wan3Models = () => {
  const [provider, setProvider] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [studioEnabled, setStudioEnabled] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [editing, setEditing] = useState(null); // 正在编辑 metadata 的模型
  const [metaText, setMetaText] = useState('');
  // 供应商对接信息编辑
  const [providerEditOpen, setProviderEditOpen] = useState(false);
  const [providerApiBase, setProviderApiBase] = useState('');
  const [providerApiKey, setProviderApiKey] = useState('');
  const [providerSaving, setProviderSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [providersRes, modelsRes] = await Promise.all([
        api.get('/admin/providers'),
        api.get('/admin/model-configs'),
      ]);
      const providers = providersRes?.data?.list || providersRes?.data || [];
      const p = providers.find(x => (x.name || '').toLowerCase() === 'ali_bj');
      setProvider(p || null);

      const list = modelsRes?.data?.list || modelsRes?.data || [];
      setModels(list.filter(m => (m.model_name || '').startsWith('WAN3.0')));
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }

    try {
      const res = await api.get('/admin/configs/key/wan3_studio_enabled');
      const v = res?.data?.config_value;
      setStudioEnabled(v === 'true' || v === '1');
    } catch { /* 配置不存在视为关闭 */ }
  };

  useEffect(() => { loadAll(); }, []);

  const toggleStudio = async (checked) => {
    setToggleLoading(true);
    try {
      await api.put('/admin/configs/key/wan3_studio_enabled', { config_value: String(checked) });
      setStudioEnabled(checked);
      message.success(checked ? '用户端 WAN3.0 菜单已开启' : '用户端 WAN3.0 菜单已关闭（API 不受影响）');
    } catch (e) {
      message.error('切换失败');
    } finally {
      setToggleLoading(false);
    }
  };

  const toggleModelStatus = async (model, status) => {
    try {
      await api.put(`/admin/model-configs/${model.id}`, { status });
      message.success(status === 'active' ? '模型已上架' : '模型已下架');
      loadAll();
    } catch (e) {
      message.error('更新失败');
    }
  };

  const openMetaEditor = (model) => {
    setEditing(model);
    setMetaText(typeof model.metadata === 'string' ? model.metadata : JSON.stringify(model.metadata || {}, null, 2));
  };

  const openProviderEditor = () => {
    setProviderApiBase(provider?.api_base || '');
    setProviderApiKey(provider?.api_key || '');
    setProviderEditOpen(true);
  };

  const saveProvider = async () => {
    if (!provider) return;
    if (!providerApiBase.trim() || !providerApiKey.trim()) {
      message.error('api_base 和 api_key 不能为空');
      return;
    }
    setProviderSaving(true);
    try {
      await api.put(`/admin/providers/${provider.id}`, {
        api_base: providerApiBase.trim(),
        api_key: providerApiKey.trim(),
      });
      message.success('对接信息已保存，网关模型缓存已刷新');
      setProviderEditOpen(false);
      loadAll();
    } catch (e) {
      message.error('保存失败');
    } finally {
      setProviderSaving(false);
    }
  };

  const saveMeta = async () => {
    try {
      JSON.parse(metaText); // 校验 JSON
    } catch {
      message.error('metadata 不是合法 JSON');
      return;
    }
    try {
      await api.put(`/admin/model-configs/${editing.id}`, { metadata: metaText });
      message.success('metadata 已保存（参数/价格即时生效）');
      setEditing(null);
      loadAll();
    } catch (e) {
      message.error('保存失败');
    }
  };

  const columns = [
    {
      title: '对外名称',
      dataIndex: 'model_name',
      render: (v) => <Text strong style={{ color: '#1677ff' }}>{v}</Text>,
    },
    {
      title: '上游模型（隐藏，不对外）',
      dataIndex: 'upstream_model_name',
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: '版本说明',
      key: 'note',
      render: (_, r) => WAN3_MODEL_INFO[r.model_name]?.note || '-',
    },
    {
      title: '基础单价 $/秒',
      dataIndex: 'input_price',
      render: (v, r) => {
        try {
          const meta = JSON.parse(typeof r.metadata === 'string' ? r.metadata : '{}');
          if (meta.resolution_pricing) {
            return Object.entries(meta.resolution_pricing).map(([k, p]) => (
              <Tag key={k}>{k}: ${p}</Tag>
            ));
          }
        } catch {}
        return v != null ? `$${v}` : '-';
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (v, r) => (
        <Switch
          checked={v === 'active'}
          checkedChildren="上架"
          unCheckedChildren="下架"
          onChange={(checked) => toggleModelStatus(r, checked ? 'active' : 'inactive')}
        />
      ),
    },
    {
      title: '参数/价格 JSON',
      key: 'meta',
      render: (_, r) => (
        <Button size="small" icon={<SettingOutlined />} onClick={() => openMetaEditor(r)}>
          编辑 metadata
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <Title level={4}>
        <VideoCameraOutlined style={{ color: '#1677ff' }} /> WAN3.0 模型（万相3.0 · 上游 阿里云百炼）
      </Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space size="large">
            <span>
              <SettingOutlined /> 用户端「WAN3.0」菜单：
              <Switch
                style={{ marginLeft: 8 }}
                checked={studioEnabled}
                loading={toggleLoading}
                onChange={toggleStudio}
                checkedChildren="已开启"
                unCheckedChildren="已关闭"
              />
            </span>
            <Tooltip title="关闭后用户端菜单与模型列表隐藏，但已持有模型名的客户仍可通过 API 调用（便于定向开通）">
              <Text type="secondary">开关说明</Text>
            </Tooltip>
          </Space>
          <Alert
            type="info"
            showIcon
            message="仅隐藏用户端菜单与模型列表；API 调用不受影响。默认关闭，调试完成后在此开启。"
            style={{ marginTop: 4 }}
          />
        </Space>
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={<span><ApiOutlined /> 上游供应商（阿里云百炼 ali_bj）</span>}
        extra={
          <Space>
            {provider && (
              <Button size="small" icon={<KeyOutlined />} onClick={openProviderEditor}>
                编辑对接信息
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>刷新</Button>
          </Space>
        }
      >
        {provider ? (
          <>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="name">{provider.name}</Descriptions.Item>
              <Descriptions.Item label="api_base">{provider.api_base}</Descriptions.Item>
              <Descriptions.Item label="api_key">
                <span style={{ fontFamily: 'monospace' }}>{(provider.api_key || '').slice(0, 12)}…</span>
              </Descriptions.Item>
              <Descriptions.Item label="status">
                <Tag color={provider.status === 'active' ? 'green' : 'red'}>{provider.status}</Tag>
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message="该供应商同时承载百炼的多个文本模型，修改 api_base / api_key 会影响这些模型，请谨慎操作。"
            />
          </>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="未找到 ali_bj 供应商（id=62），请先确认供应商是否被改名或删除"
          />
        )}
      </Card>

      <Card
        size="small"
        title="模型配置（对外名 WAN3.0 系列，上游 wan3.0-* 对用户不可见）"
      >
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={models}
          columns={columns}
          pagination={false}
        />
      </Card>

      <Modal
        open={providerEditOpen}
        title={<span><KeyOutlined /> 编辑上游对接信息（阿里云百炼）</span>}
        onOk={saveProvider}
        onCancel={() => setProviderEditOpen(false)}
        confirmLoading={providerSaving}
        okText={<span><SaveOutlined /> 保存</span>}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Text strong>api_base（上游接口地址）</Text>
            <Input
              style={{ marginTop: 6 }}
              value={providerApiBase}
              onChange={e => setProviderApiBase(e.target.value)}
              placeholder="https://{WorkspaceId}.cn-beijing.maas.aliyuncs.com/compatible-mode/v1"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              视频合成接口由网关取该地址的同域 origin 后拼接 DashScope 路径，无需填写完整路径。
            </Text>
          </div>
          <div>
            <Text strong>api_key（上游对接密钥）</Text>
            <Input.Password
              style={{ marginTop: 6 }}
              value={providerApiKey}
              onChange={e => setProviderApiKey(e.target.value)}
              placeholder="sk-ws-..."
              visibilityToggle
            />
          </div>
          <Alert
            type="warning"
            showIcon
            message="该供应商同时承载百炼的多个文本模型，保存后立即生效（网关模型缓存自动刷新）。"
          />
        </Space>
      </Modal>

      <Modal
        open={!!editing}
        title={`编辑 metadata — ${editing?.model_name || ''}（参数与价格随 JSON 即时生效）`}
        onOk={saveMeta}
        onCancel={() => setEditing(null)}
        width={720}
        okText={<span><SaveOutlined /> 保存</span>}
      >
        <TextArea
          rows={18}
          value={metaText}
          onChange={e => setMetaText(e.target.value)}
          style={{ fontFamily: 'monospace', fontSize: 12 }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          关键字段：resolutions（分辨率选项，取值须为 480P/720P/1080P）、resolution_pricing（$/秒单价）、
          durationRange、ratios、maxRefImages/Videos/Audios、videoModes、tier、features、desc。改价只需改 resolution_pricing。
        </Text>
      </Modal>
    </div>
  );
};

export default Wan3Models;
